<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useProjectsStore } from '@/stores/projects'
import { useI18n } from 'vue-i18n'
import { useBefehle, type Befehl } from '@/composables/useBefehle'
import { useSprache } from '@/composables/useSprache'
import AppHeader from '@/components/AppHeader.vue'
import BestaetigungModal from '@/components/BestaetigungModal.vue'
import NeuerungenKarte from '@/components/NeuerungenKarte.vue'
import UpdateBanner from '@/components/UpdateBanner.vue'

const route = useRoute()
const router = useRouter()
const isFullscreen = computed(() => route.meta?.fullscreen === true)

/* ===== Was die Suche überall anbietet (Cmd+K) =========================
 *
 * Die Projekte gehören hierher und nicht in die Editor-Ansicht: Man will aus
 * einem Projekt heraus in ein anderes springen können, ohne den Umweg über die
 * Liste. Geladen wird erst, wenn jemand die Suche das erste Mal öffnet — die
 * Hülle steht auf jeder Seite, und ein Aufruf beim Start würde jeden Aufbau
 * bremsen.
 */
const { t } = useI18n()
const projekte = useProjectsStore()
const sprache = useSprache()
const befehle = useBefehle()
watch(befehle.offen, (auf) => {
  if (auf && projekte.projects.length === 0 && !projekte.loading) void projekte.loadAll()
})
befehle.registriere('projekte', () => projekte.projects.map((p): Befehl => ({
  id: `projekt:${p.id}`,
  label: p.name,
  gruppe: t('suche.gruppe_projekte'),
  tun: () => { void router.push(`/projects/${p.id}/edit`) },
})))

/* Die Sprache der Oberfläche ist über die Suche erreichbar, nicht nur im Konto.
   Wer sie sucht, sucht nach „English" oder „Deutsch" — beides steht als Wort im
   Eintrag, in beiden Sprachen. */
befehle.registriere('sprache', () => [{
  id: 'sprache:umschalten',
  label: sprache.gewaehlt.value === 'de' ? 'Switch to English' : 'Auf Deutsch umstellen',
  gruppe: t('suche.gruppe_handgriffe'),
  hinweis: t('konto.sprache'),
  tun: () => sprache.setze(sprache.gewaehlt.value === 'de' ? 'en' : 'de'),
}])
</script>

<template>
  <!-- Fullscreen-Mode: kein AppShell-Chrome (Editor hat eigene AppHeader).
       h-full statt min-h-svh: nimmt den Platz, den der Eltern-Flex vorgibt
       (Viewport minus Staging-Banner) — sonst 26px Überlauf. Als Spalte, damit
       die Neuerungen-Karte darüber Platz nimmt statt den Editor zu überlappen;
       ohne Karte füllt der Editor alles wie vorher. -->
  <div v-if="isFullscreen" class="h-full flex flex-col bg-bg text-fg">
    <NeuerungenKarte class="m-3 shrink-0" />
    <div class="flex-1 min-h-0">
      <slot />
    </div>
    <UpdateBanner />
    <BestaetigungModal />
  </div>

  <!-- Standard-Mode: AppHeader + Main. Phase Y.10/Y.11: kompakt im Dashboard
       für konsistenten Look mit Editor (User-Präferenz). -->
  <div v-else class="min-h-svh flex flex-col bg-bg text-fg">
    <AppHeader />
    <main class="flex-1 min-w-0 p-4 md:p-6 lg:p-8">
      <NeuerungenKarte class="mb-4 md:mb-6" />
      <slot />
    </main>
    <UpdateBanner />
    <BestaetigungModal />
  </div>
</template>
